/**
 * Kapabilitas yang **benar-benar mengubah perilaku engine**.
 *
 * ⚠️ Sengaja SEDIKIT. Daftar kapabilitas yang tumbuh spekulatif menjadi matriks kombinasi yang tak
 * pernah diuji — dan matriks yang tak diuji lebih berbahaya daripada cabang `if` yang jujur.
 * Kapabilitas baru hanya ditambahkan ketika ada perilaku nyata yang bergantung padanya.
 */
/**
 * ⭐⭐ **MODE JUAL — keputusan yang diambil SEBELUM outlet dibuka** (Imron, 2026-08-02).
 *
 * ### ⛔ Kenapa ini kolom wajib-isi, bukan "ada/tidaknya baris kapabilitas"
 * Sebelumnya mode jual dibaca dari ADA-TIDAKNYA baris `outlet_capability = 'table_service'`.
 * Itu persis pola yang Guardrail #2 larang: klasifikasi lewat **konvensi**, bukan enum wajib-isi.
 * Akibatnya, outlet yang belum pernah disetel **terbaca sebagai quick service** — sebuah jawaban
 * yang tak pernah dipilih siapa pun, dan yang di resto berarti dashboard mejanya lenyap tanpa
 * satu pun pesan.
 *
 * ⇒ Mode jual sekarang **kolom NOT NULL di `outlet`**. Ia harus dijawab saat outlet dibuat, dan
 *   layar tahu bentuk mana yang harus dimuat sebelum kasir mengetuk apa pun.
 *
 * ⚠️ Ia **bukan** jenis usaha dan **bukan** kapabilitas: quick service pun tetap boleh punya
 * `kitchen_routing` — pesanannya tetap turun ke griller dan printer bar; yang tak ada cuma mejanya.
 */
export const SALE_MODES = Object.freeze([
    /** Pesanan menempel pada meja fisik dan hidup berjam-jam sebelum ditagih. */
    'table_service',
    /** Pesan–bayar di konter; semua meja `virtual_quick` (§4). */
    'quick_service',
]);
export const OUTLET_CAPABILITIES = Object.freeze([
    /** Baris wajib melewati `order.sent` sebelum bill — batas rezim kontrol void (§10). */
    'kitchen_routing',
    /** Item dicari lewat barcode/SKU, bukan diramban dari kisi menu. */
    'barcode_lookup',
    /** Kuantitas datang dari timbangan (jual per berat, §2 `QTY` desimal). */
    'weighing_scale',
    /** Harga bertingkat menurut kuantitas (eceran vs grosir). */
    'tiered_price',
    /** Stok berkurang lewat RESEP (teoretis, §9). Khas F&B. */
    'recipe_backflush',
    /** Stok berkurang LANGSUNG per SKU saat terjual (aktual, §9). Khas retail. */
    'direct_stock_deduct',
]);
/**
 * ⭐⭐⭐ **Kapabilitas yang MASIH BELUM dibaca satu baris kode pun** (audit 2026-08-23).
 *
 * ### ⛔ Kenapa daftar ini ada, dan kenapa ia bukan sekadar TODO
 * Audit menemukan **5 dari 7** kapabilitas disetel manajer di portal lalu tak berakibat apa pun:
 * `barcode_lookup`, `weighing_scale`, `tiered_price`, `recipe_backflush`, `direct_stock_deduct`.
 * Preset `resto_kertas` (K18) bahkan MENJUAL `recipe_backflush` sebagai isinya.
 *
 * Ini penyakit yang sama persis dengan `promo.requires_approval` sebelum K23 — **setelan yang
 * berbohong kepada orang yang menyetelnya** — cuma sekarang lima kali sekaligus. Dan setelan yang
 * berbohong lebih buruk daripada setelan yang tak ada: yang menyalakannya berhenti mencari sebab
 * lain ketika hasilnya tak muncul.
 *
 * ### ⭐ Yang dipilih: DINYATAKAN, bukan dicopot
 * Mencopotnya dari enum berarti migrasi enum Postgres yang mahal DAN membuang keputusan desain yang
 * memang sudah benar (§9 membedakan potong-stok-lewat-resep dari potong-stok-langsung; itu
 * pembedaan struktural yang tak boleh hilang cuma karena mesinnya belum ditulis).
 *
 * ⇒ Ia tetap ada, TAPI layar menandainya **"belum aktif"** dan uji struktural `CAP1` menjaga daftar
 *   ini tetap jujur: begitu sebuah kapabilitas mulai dibaca kode, ia WAJIB keluar dari sini.
 *   Daftar yang menua diam-diam akan berbohong ke arah sebaliknya.
 */
export const CAPABILITIES_NOT_IMPLEMENTED = Object.freeze([
    'barcode_lookup',
    'weighing_scale',
    'tiered_price',
]);
const NOT_IMPL_SET = new Set(CAPABILITIES_NOT_IMPLEMENTED);
/**
 * `true` ⇒ menyalakannya di portal **tak berakibat apa pun hari ini**.
 * ⚠️ Dipakai layar untuk menandainya, bukan untuk menyembunyikannya: yang disembunyikan tak bisa
 * ditanyakan, dan pemilik yang bertanya "kenapa stok tak berkurang?" berhak melihat jawabannya.
 */
export function isCapabilityImplemented(c) {
    return !NOT_IMPL_SET.has(c);
}
const CAP_SET = new Set(OUTLET_CAPABILITIES);
/** Guard {@link OutletCapability}. */
export function isOutletCapability(v) {
    return typeof v === 'string' && CAP_SET.has(v);
}
/**
 * Preset onboarding — **template default**, bukan klasifikasi struktural.
 *
 * ⚠️ Bedakan tegas dari `business_type`: preset menentukan **nilai awal** saat outlet dibuat;
 * setelah itu kapabilitas bisa diubah per outlet tanpa mengubah presetnya. Preset yang dijadikan
 * sumber kebenaran runtime akan mengunci tenant pada keputusan hari pertamanya.
 */
export const OUTLET_PRESETS = Object.freeze([
    'resto_full',
    'resto_quick',
    'coffee_shop',
    'warteg',
    'minimarket',
    'toko_bangunan',
    /**
     * ⭐⭐⭐ **Resto berkertas** (Imron, 2026-08-03) — pesanan ditulis tangan: satu salinan ke dapur,
     * satu ke kasir. Kasir baru bekerja saat tamu minta bill, lalu memasukkan seluruh isi kertas
     * berikut yang belum sempat tercatat.
     */
    'resto_kertas',
]);
const PRESET_SET = new Set(OUTLET_PRESETS);
/** Guard {@link OutletPreset}. */
export function isOutletPreset(v) {
    return typeof v === 'string' && PRESET_SET.has(v);
}
export const PRESET_SPEC = Object.freeze({
    resto_full: {
        saleMode: 'table_service',
        businessType: 'fnb',
        capabilities: ['kitchen_routing', 'recipe_backflush'],
        taxSuggestion: ['service_charge', 'local_tax'],
        reason: 'Resto full-service seperti Arena: tamu duduk, pesanan ke dapur bertahap, service charge ' +
            'lazim. Ini pusat rancangan produk (PRD §2).',
    },
    resto_quick: {
        saleMode: 'quick_service',
        businessType: 'fnb',
        capabilities: ['kitchen_routing', 'recipe_backflush'],
        taxSuggestion: ['local_tax'],
        reason: 'Quick-service: tanpa meja, tapi dapur tetap perlu tiket. Di VTEC ini dipaksa memakai meja ' +
            'bernomor ajaib \'100\'; di sini cukup memilih sale_mode `quick_service` (§4).',
    },
    coffee_shop: {
        saleMode: 'quick_service',
        businessType: 'fnb',
        capabilities: ['kitchen_routing', 'recipe_backflush'],
        taxSuggestion: ['local_tax'],
        reason: 'Barista perlu tiket (bar = dapur), modifier berat (less ice/extra shot), takeaway dominan. ' +
            'Meja opsional — banyak yang berpindah ke table_service belakangan.',
    },
    warteg: {
        saleMode: 'quick_service',
        businessType: 'fnb',
        capabilities: [],
        taxSuggestion: [],
        reason: 'Warung makan kecil: ambil-bayar, tanpa tiket dapur, tanpa service charge. Banyak yang ' +
            'non-PKP dan di bawah ambang PB1 ⇒ saran pajak KOSONG, bukan ditebak.',
    },
    minimarket: {
        saleMode: 'quick_service',
        businessType: 'retail',
        capabilities: ['barcode_lookup', 'direct_stock_deduct'],
        taxSuggestion: [],
        reason: 'Warung kelontong/minimarket: barcode jadi input utama, stok berkurang LANGSUNG per SKU ' +
            '(aktual), bukan lewat resep. Tanpa dapur ⇒ tanpa kitchen_routing.',
    },
    /**
     * ⭐⭐ Resto full-service TANPA tiket sistem — tiketnya kertas.
     *
     * ⛔ Kenapa TANPA `kitchen_routing`: kapabilitas itu berarti "sistem yang mengantar tiket ke
     * dapur", dan di sini kertaslah yang mengantar. Menyalakannya menuntut stasiun cetak yang tak
     * ada, lalu `send` menolak (`ORDER-409-NO_ROUTE`) dan outletnya tak bisa berjualan sama sekali.
     *
     * ⚠️ `quick_service` bukan berarti "warung": di MATA SISTEM pesanannya memang lahir di konter
     * saat tamu minta bill dan ditutup beberapa detik kemudian — tak pernah ada pesanan yang hidup
     * berjam-jam menempel pada meja. Akibat yang harus disadari: **pandangan lantai tak tersedia**,
     * karena sistem baru tahu ada tamu di meja 7 saat billnya dibuat.
     *
     * ⭐ `recipe_backflush` TETAP menyala (Imron: "stok tetap potong resep") — potong stok terjadi
     * saat bayar, bukan saat kirim, jadi ia tak bergantung pada tiket sama sekali.
     */
    resto_kertas: {
        saleMode: 'quick_service',
        businessType: 'fnb',
        capabilities: ['recipe_backflush'],
        taxSuggestion: ['service_charge', 'local_tax'],
        reason: 'Resto berpelayan yang tiketnya ditulis tangan: satu salinan ke dapur, satu ke kasir. Kasir ' +
            'memasukkan semuanya saat tamu minta bill. Service charge & PB1 tetap lazim — itu yang ' +
            'membedakannya dari warteg, yang saran pajaknya sengaja KOSONG.',
    },
    toko_bangunan: {
        saleMode: 'quick_service',
        businessType: 'retail',
        capabilities: ['barcode_lookup', 'direct_stock_deduct', 'tiered_price', 'weighing_scale'],
        taxSuggestion: ['vat'],
        reason: 'Dijual per meter/kg/batang/sak ⇒ QTY desimal (§2) dan timbangan. Harga grosir vs eceran ' +
            '⇒ tiered_price. Lebih sering PKP ⇒ saran PPN, bukan PB1.',
    },
});
/** Kapabilitas bawaan sebuah preset. */
export function presetCapabilities(p) {
    return PRESET_SPEC[p].capabilities;
}
/**
 * Apakah outlet ini **mewajibkan meja** saat pesanan dibuka?
 *
 * ⭐ Ditanyakan pada MODE JUAL, bukan pada `business_type` maupun kapabilitas. Warteg dan
 * minimarket sama-sama tak bermeja meski jenis usahanya berbeda — dan resto yang beralih ke
 * quick service ikut benar tanpa cabang tambahan.
 */
export function needsTable(mode) {
    return mode === 'table_service';
}
/** Mode jual bawaan sebuah preset. */
export function presetSaleMode(p) {
    return PRESET_SPEC[p].saleMode;
}
/** Apakah baris wajib melewati `order.sent` sebelum bisa ditagihkan? */
export function needsKitchenSend(caps) {
    return caps.includes('kitchen_routing');
}
/**
 * Cara utama menemukan item di layar kasir.
 *
 * ⚠️ Ini memilih **shell**, bukan mesin: kisi menu dan pencarian barcode berjalan di atas jalur
 * order-to-pay yang **sama persis** (K4 — peran adalah shell berbeda di atas inti yang sama).
 */
export function itemLookupMode(caps) {
    return caps.includes('barcode_lookup') ? 'barcode' : 'menu_grid';
}
/**
 * Cara stok berkurang. `undefined` = outlet ini tak mengurangi stok sama sekali (mis. warteg yang
 * belum memakai modul persediaan).
 *
 * ⛔ Keduanya **tak boleh** menyala bersamaan: stok yang dikurangi dua kali menghasilkan selisih
 * yang tampak seperti kebocoran barang, dan itu tuduhan yang salah kepada orang.
 */
export function modeStok(caps) {
    const resep = caps.includes('recipe_backflush');
    const langsung = caps.includes('direct_stock_deduct');
    if (resep && langsung) {
        throw new Error('recipe_backflush dan direct_stock_deduct tak boleh menyala bersamaan — stok akan ' +
            'berkurang dua kali, dan selisihnya tampak seperti kebocoran barang (tuduhan yang salah).');
    }
    return resep ? 'recipe_backflush' : langsung ? 'direct_stock_deduct' : undefined;
}
/**
 * ⭐⭐ **Jenis tanda saat pesanan DITAHAN** (Imron, 2026-08-03).
 *
 * Kasir menyisihkan pesanan yang belum selesai lalu memanggilnya lagi. Tandanya **struktural**,
 * bukan satu kotak teks bebas:
 * - `table` — menunjuk baris `dining_table` yang SUNGGUHAN. ⭐ Inilah sebabnya ia dipisah: "Meja 7"
 *   sebagai teks tak nyambung ke mejanya, dan pandangan lantai maupun pindah-meja tak akan pernah
 *   bisa dibangun di atas data seperti itu tanpa membersihkannya satu per satu.
 * - `name` — nama tamu ("Bu Rina").
 * - `queue` — nomor antrean; boleh diketik, boleh diberikan sistem bila kasir tak mengisi apa pun.
 */
export const PARK_KINDS = Object.freeze(['table', 'name', 'queue']);
/* ────────────────────────────────────────────────────────────────────────────
 * Stasiun cetak / KDS — TUJUAN pesanan saat dikirim
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * ⭐⭐ **Jenis stasiun.** Bukan cuma "printer": KDS adalah layar, dan keduanya sama-sama tujuan
 * rute yang sah. Menyatukannya dalam satu tabel membuat pemindahan stasiun dari printer ke KDS
 * (yang sering terjadi saat outlet direnovasi) jadi perubahan SATU kolom, bukan migrasi rute.
 *
 * ⚠️ `summary_printer` & `checker_printer` bukan tempat memasak: yang pertama mencetak ringkasan
 * satu bill utuh untuk penyelia, yang kedua salinan pemeriksa. Keduanya menerima SEMUA baris —
 * dan itu sebabnya mereka bukan sekadar "printer dapur nomor dua".
 */
export const PRINT_STATION_KINDS = Object.freeze([
    'kds',
    'prep_printer',
    'summary_printer',
    'checker_printer',
    /**
     * ⭐⭐⭐ **Tujuan yang TAK MENERBITKAN TIKET** — "diambil di konter" (Imron, 2026-08-03).
     *
     * Rokok, botolan, merchandise: tak ada yang menyiapkannya. Sebelum ada ini, outlet ber-dapur
     * hanya punya dua nasib untuk barang seperti itu — tiket sampah tercetak di dapur (lewat stasiun
     * cadangan), atau SELURUH pesanan ditolak karena ada rokok di dalamnya.
     *
     * ⛔ Ia menyatakan **"sengaja tanpa tiket"**, dan itu HARUS berbeda dari **"belum dirutekan"**.
     * Yang kedua wajib tetap ditolak keras: menu baru yang lupa dirutekan tak boleh diam-diam
     * dianggap tak perlu dimasak.
     *
     * ⚠️ Dan ia **bukan** "tiket dibawa tangan". Resto berkertas tetap MENYIAPKAN barangnya; yang
     * tak ada di sana cuma pencetaknya. Menyatukan keduanya membuat pertanyaan "produk mana yang
     * sebenarnya masuk dapur?" tak bisa dijawab lagi — dan itu pertanyaan yang menentukan beban
     * dapur, SLA, serta pemasangan KDS kelak.
     */
    'counter',
]);
const PRINT_STATION_KIND_SET = new Set(PRINT_STATION_KINDS);
/**
 * Guard {@link PrintStationKind}.
 *
 * ⚠️ Ditambahkan 2026-08-05 — enum ini satu-satunya di `business.ts` yang belum punya guard,
 * jadi tiap pemanggil yang menerima `kind` dari SQL terpaksa meng-`as`-nya. `as` adalah janji yang
 * tak diperiksa siapa pun: nilai enum baru yang belum dikenal klien lolos begitu saja, dan
 * `emitsTicket` menjawabnya dengan `true` (karena `!== 'counter'`) — persis jawaban yang salah.
 */
export function isPrintStationKind(v) {
    return typeof v === 'string' && PRINT_STATION_KIND_SET.has(v);
}
/** Stasiun yang benar-benar MEMASAK — hanya ini yang butuh rute per produk. */
export function isPrepStation(kind) {
    return kind === 'kds' || kind === 'prep_printer';
}
/**
 * ⭐ Apakah stasiun ini **menerbitkan pekerjaan cetak**?
 *
 * ⛔ `counter` TIDAK. Ia tetap tujuan yang sah — barisnya punya jawaban atas "ke mana?" dan karena
 * itu tak lagi yatim — tapi tak ada kertas maupun layar yang menerimanya. Klien yang mencetak ke
 * sana akan menghasilkan struk kosong di printer yang tak pernah dibaca siapa pun.
 */
export function emitsTicket(kind) {
    return kind !== 'counter';
}
//# sourceMappingURL=business.js.map